import React, { useState, useEffect } from 'react';
import { getAllAgents, getAllBureaux, getAllCellules } from '../services/api';

const Statistiques = () => {
  const [stats, setStats] = useState({
    total: 0,
    hommes: 0,
    femmes: 0,
    actifs: 0,
    inactifs: 0,
    bureaux: 0,
    cellules: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      // Charger les données en parallèle
      const [agents, bureaux, cellules] = await Promise.all([
        getAllAgents(),
        getAllBureaux(),
        getAllCellules()
      ]);
      
      const statsData = agents.reduce(
        (acc, agent) => {
          acc.total++;
          if (agent.sexe === 'M') acc.hommes++;
          if (agent.sexe === 'F') acc.femmes++;
          if (agent.statut === 'actif') acc.actifs++;
          if (agent.statut === 'inactif') acc.inactifs++;
          return acc;
        },
        { total: 0, hommes: 0, femmes: 0, actifs: 0, inactifs: 0 }
      );

      // Utiliser le vrai total depuis la base de données
      statsData.bureaux = Array.isArray(bureaux) ? bureaux.length : 0;
      statsData.cellules = Array.isArray(cellules) ? cellules.length : 0;
      setStats(statsData);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-text-light">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  const hommesPercent = stats.total > 0 ? (stats.hommes / stats.total) * 100 : 0;
  const femmesPercent = stats.total > 0 ? (stats.femmes / stats.total) * 100 : 0;
  const actifsPercent = stats.total > 0 ? (stats.actifs / stats.total) * 100 : 0;
  const inactifsPercent = stats.total > 0 ? (stats.inactifs / stats.total) * 100 : 0;

  return (
    <div className="p-8 bg-background min-h-screen">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-text mb-8 flex items-center">
          <i className="fas fa-chart-bar mr-3 text-primary"></i>
          Statistiques
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Total Agents */}
          <div className="bg-cardBg rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-light text-sm mb-1">Total Agents</p>
                <p className="text-3xl font-bold text-primary">{stats.total}</p>
              </div>
              <i className="fas fa-users text-4xl text-primary opacity-20"></i>
            </div>
          </div>

          {/* Hommes */}
          <div className="bg-cardBg rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-light text-sm mb-1">Hommes</p>
                <p className="text-3xl font-bold text-blue-600">{stats.hommes}</p>
                <p className="text-xs text-text-light mt-1">{hommesPercent.toFixed(1)}%</p>
              </div>
              <i className="fas fa-mars text-4xl text-blue-600 opacity-20"></i>
            </div>
          </div>

          {/* Femmes */}
          <div className="bg-cardBg rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-light text-sm mb-1">Femmes</p>
                <p className="text-3xl font-bold text-pink-600">{stats.femmes}</p>
                <p className="text-xs text-text-light mt-1">{femmesPercent.toFixed(1)}%</p>
              </div>
              <i className="fas fa-venus text-4xl text-pink-600 opacity-20"></i>
            </div>
          </div>

          {/* Actifs */}
          <div className="bg-cardBg rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-light text-sm mb-1">Actifs</p>
                <p className="text-3xl font-bold text-green-600">{stats.actifs}</p>
                <p className="text-xs text-text-light mt-1">{actifsPercent.toFixed(1)}%</p>
              </div>
              <i className="fas fa-check-circle text-4xl text-green-600 opacity-20"></i>
            </div>
          </div>

          {/* Inactifs */}
          <div className="bg-cardBg rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-light text-sm mb-1">Inactifs</p>
                <p className="text-3xl font-bold text-red-600">{stats.inactifs}</p>
                <p className="text-xs text-text-light mt-1">{inactifsPercent.toFixed(1)}%</p>
              </div>
              <i className="fas fa-times-circle text-4xl text-red-600 opacity-20"></i>
            </div>
          </div>

          {/* Bureaux */}
          <div className="bg-cardBg rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-light text-sm mb-1">Bureaux</p>
                <p className="text-3xl font-bold text-purple-600">{stats.bureaux}</p>
              </div>
              <i className="fas fa-building text-4xl text-purple-600 opacity-20"></i>
            </div>
          </div>

          {/* Cellules */}
          <div className="bg-cardBg rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-light text-sm mb-1">Cellules</p>
                <p className="text-3xl font-bold text-indigo-600">{stats.cellules}</p>
              </div>
              <i className="fas fa-sitemap text-4xl text-indigo-600 opacity-20"></i>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Répartition par sexe */}
          <div className="bg-cardBg rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-text mb-4">Répartition par Sexe</h2>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-text">Hommes</span>
                  <span className="text-sm font-medium text-text">{stats.hommes}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-blue-600 h-4 rounded-full transition-all"
                    style={{ width: `${hommesPercent}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-text">Femmes</span>
                  <span className="text-sm font-medium text-text">{stats.femmes}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-pink-600 h-4 rounded-full transition-all"
                    style={{ width: `${femmesPercent}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Répartition par statut */}
          <div className="bg-cardBg rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-text mb-4">Répartition par Statut</h2>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-text">Actifs</span>
                  <span className="text-sm font-medium text-text">{stats.actifs}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-green-600 h-4 rounded-full transition-all"
                    style={{ width: `${actifsPercent}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-text">Inactifs</span>
                  <span className="text-sm font-medium text-text">{stats.inactifs}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-red-600 h-4 rounded-full transition-all"
                    style={{ width: `${inactifsPercent}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistiques;

